import { gql, TypedDocumentNode, useMutation, useQuery } from "@apollo/client";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import {
  Mutation,
  MutationDeleteReportRecipientArgs,
  MutationSaveReportRecipientArgs,
  Query,
  ReportRecipientRow,
} from "_graphql-types";
import { useState } from "react";
import { useNotify } from "react-admin";
import { EmptyState, Page, QueryState, Section } from "../clientInvestments/shared";
import { recipientsFromText, recipientsToText } from "./logic";

const REPORT_RECIPIENTS: TypedDocumentNode<Pick<Query, "reportRecipients">> = gql`
  query ReportRecipients {
    reportRecipients {
      reportId
      reportName
      recipients
    }
  }
`;

const SAVE_REPORT_RECIPIENT: TypedDocumentNode<Pick<Mutation, "saveReportRecipient">, MutationSaveReportRecipientArgs> = gql`
  mutation SaveReportRecipient($input: ReportRecipientInput!) {
    saveReportRecipient(input: $input) {
      reportId
    }
  }
`;

const DELETE_REPORT_RECIPIENT: TypedDocumentNode<Pick<Mutation, "deleteReportRecipient">, MutationDeleteReportRecipientArgs> = gql`
  mutation DeleteReportRecipient($reportId: Int!) {
    deleteReportRecipient(reportId: $reportId)
  }
`;

interface Draft {
  reportName: string;
  recipients: string;
}

const draftOf = (r: Pick<ReportRecipientRow, "reportName" | "recipients">): Draft => ({
  reportName: r.reportName,
  recipients: recipientsToText(r.recipients ?? []),
});

/** One report's row: edit in place, save or delete it. reportId null is the "add a report" row. */
function RecipientRow({ row, onDone }: { row: ReportRecipientRow | null; onDone: () => Promise<unknown> }) {
  const notify = useNotify();
  const initial = row ? draftOf(row) : { reportName: "", recipients: "" };
  const [draft, setDraft] = useState<Draft>(initial);
  const [save, saving] = useMutation(SAVE_REPORT_RECIPIENT);
  const [remove, removing] = useMutation(DELETE_REPORT_RECIPIENT);
  const changed = draft.reportName !== initial.reportName || draft.recipients !== initial.recipients;

  const run = async (work: () => Promise<unknown>, done: string) => {
    try {
      await work();
      await onDone();
      if (!row) setDraft({ reportName: "", recipients: "" });
      notify(done, { type: "success" });
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
    }
  };
  const onSave = () =>
    run(
      () =>
        save({
          variables: {
            input: { reportId: row?.reportId ?? null, reportName: draft.reportName, recipients: recipientsFromText(draft.recipients) },
          },
        }),
      row ? `Saved ${draft.reportName}` : `Added ${draft.reportName}`
    );
  const onDelete = () =>
    row && window.confirm(`Delete ${row.reportName} and its recipients?`) && run(() => remove({ variables: { reportId: row.reportId } }), `Deleted ${row.reportName}`);

  return (
    <TableRow sx={changed ? { bgcolor: "action.hover" } : undefined}>
      <TableCell sx={{ width: 320, verticalAlign: "top" }}>
        <TextField
          size="small"
          fullWidth
          placeholder={row ? undefined : "New report name"}
          value={draft.reportName}
          onChange={e => setDraft({ ...draft, reportName: e.target.value })}
          inputProps={{ "aria-label": "Report name" }}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={1}
          maxRows={8}
          placeholder="One address per line"
          value={draft.recipients}
          onChange={e => setDraft({ ...draft, recipients: e.target.value })}
          inputProps={{ "aria-label": "Recipients" }}
        />
      </TableCell>
      <TableCell sx={{ width: 220, verticalAlign: "top" }}>
        <Stack direction="row" spacing={1}>
          {row && changed && <Button onClick={() => setDraft(initial)}>Discard</Button>}
          <Button variant="contained" disabled={!changed || saving.loading || draft.reportName.trim() === ""} onClick={onSave}>
            {row ? "Save" : "Add"}
          </Button>
          {row && (
            <Button color="error" disabled={removing.loading} onClick={onDelete}>
              Delete
            </Button>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

/**
 * Report Center: who each scheduled report is mailed to (#4689), replacing the legacy Report
 * Center's recipients grid. RockCreek's reporting team only.
 */
export function ReportRecipients() {
  const { data, loading, error, refetch } = useQuery(REPORT_RECIPIENTS);
  const rows = data?.reportRecipients ?? [];
  return (
    <Page title="Report Recipients" description="Who each scheduled report is emailed to. Enter one address per line.">
      <Section title="Reports">
        <QueryState loading={loading} error={error} />
        {!loading && !error && rows.length === 0 && <EmptyState>No reports yet; add one below.</EmptyState>}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Report</TableCell>
              <TableCell>Recipients</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Keyed by the stored values too, so a refetch resets a saved row's draft. */}
            {rows.map(r => (
              <RecipientRow key={`${r.reportId}:${r.reportName}:${(r.recipients ?? []).join(";")}`} row={r} onDone={refetch} />
            ))}
            <RecipientRow key="new" row={null} onDone={refetch} />
          </TableBody>
        </Table>
      </Section>
    </Page>
  );
}
